import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export function useTableManagement() {
  const queryClient = useQueryClient();

  const tablesQuery = useQuery({
    queryKey: ['tables'],
    queryFn: () => api.getTables(),
  });

  const createTableMutation = useMutation({
    mutationFn: (payload: { name: string; location?: string }) => api.createTable(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });

  const updateTableMutation = useMutation({
    mutationFn: ({ tableId, payload }: { tableId: string; payload: { name?: string; location?: string } }) =>
      api.updateTable(tableId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });

  const deleteTableMutation = useMutation({
    mutationFn: (tableId: string) => api.deleteTable(tableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });

  return {
    tables: tablesQuery.data?.tables || [],
    isLoading: tablesQuery.isLoading,
    isError: tablesQuery.isError,
    error: tablesQuery.error,
    createTable: createTableMutation.mutateAsync,
    updateTable: updateTableMutation.mutateAsync,
    deleteTable: deleteTableMutation.mutateAsync,
    isCreating: createTableMutation.isPending,
    isUpdating: updateTableMutation.isPending,
    isDeleting: deleteTableMutation.isPending,
  };
}

export function useTable(tableId: string) {
  return useQuery({
    queryKey: ['tables', tableId],
    queryFn: () => api.getTable(tableId),
    enabled: !!tableId,
  });
}
